import { useCallback, useEffect, useRef, useState } from "react";
import { todayInShanghai } from "@longjie/shared";
import {
  Check,
  FileArrowDown,
  PencilSimple,
  Package,
  Stack,
  UploadSimple,
  UsersThree,
  XCircle,
} from "@phosphor-icons/react";
import { ApiError, download, request, saveBlob } from "./api";
import { Button, Field } from "./components";

type ImportType = "customers" | "products" | "purchases";
type PurchaseDefaults = {
  occurredOn: string;
  referenceNo: string;
};
type PurchasePreviewDefaults = PurchaseDefaults & { counterparty?: string };
type PurchaseSource = {
  sheetName: string;
  headerRow: number;
  columns: {
    skuCode?: string;
    productCode?: string;
    code?: string;
    genericCode?: string;
    productName?: string;
    specification?: string;
    quantity: string;
    unit?: string;
    occurredOn?: string;
    counterparty?: string;
    referenceNo?: string;
    note?: string;
  };
};
type PurchaseRow = {
  rowIndex?: number;
  occurredOn: string;
  counterparty: string;
  referenceNo: string;
  skuCode: string;
  productCode?: string;
  quantity: number;
  note: string;
  sheetName?: string;
  rowNumber?: number;
  sourceProductName?: string;
  sourceSkuCode?: string;
  sourceProductCode?: string;
  sourceCode?: string;
  sourceSpecification?: string;
  sourceUnit?: string;
  productName?: string;
  specification?: string;
  unit?: string;
  warnings?: string[];
};
type PurchaseDraftRow = Omit<PurchaseRow, "quantity"> & { rowIndex: number; quantity: string };
type ImportPreviewResponse = {
  jobId: string;
  valid: boolean;
  errors: string[];
  revision?: number;
  preview: {
    customers?: unknown[];
    products?: unknown[];
    purchases?: PurchaseRow[];
    documentCount?: number;
    totalQuantity?: number;
    sources?: PurchaseSource[];
    defaults?: PurchasePreviewDefaults;
    warnings?: string[];
    revision?: number;
  };
};
type ImportPreview = {
  type: ImportType;
  jobId: string;
  valid: boolean;
  errors: string[];
  count: number;
  purchases: PurchaseRow[];
  documentCount: number;
  totalQuantity: number;
  sources: PurchaseSource[];
  defaults: PurchasePreviewDefaults | undefined;
  warnings: string[];
  revision: number;
};

function purchaseRowLabel(row: PurchaseRow | PurchaseDraftRow, index: number) {
  return row.sheetName && row.rowNumber
    ? `${row.sheetName} 第 ${row.rowNumber} 行`
    : `第 ${(row.rowIndex ?? index) + 1} 条明细`;
}

function purchaseDraftRows(rows: PurchaseRow[]): PurchaseDraftRow[] {
  return rows.map((row, index) => ({ ...row, rowIndex: row.rowIndex ?? index, quantity: String(row.quantity) }));
}

function importPreview(type: ImportType, result: ImportPreviewResponse): ImportPreview {
  const purchases = result.preview.purchases ?? [];
  const rows = type === "purchases" ? purchases : result.preview[type] ?? [];
  return {
    type,
    jobId: result.jobId,
    valid: result.valid && result.errors.length === 0,
    errors: result.errors,
    count: rows.length,
    purchases,
    documentCount: result.preview.documentCount ?? new Set(purchases.map((row) =>
      JSON.stringify([row.occurredOn, row.counterparty, row.referenceNo]),
    )).size,
    totalQuantity: result.preview.totalQuantity ?? purchases.reduce((sum, row) => sum + row.quantity, 0),
    sources: result.preview.sources ?? [],
    defaults: result.preview.defaults,
    warnings: result.preview.warnings ?? [],
    revision: result.revision ?? result.preview.revision ?? 0,
  };
}

const importCards = [
  {
    type: "customers",
    eyebrow: "客户与地址",
    title: "客户账号导入",
    description: "导入客户资料和地址，确认后自动生成一次性初始密码。",
    filename: "客户导入模板.xlsx",
    icon: UsersThree,
  },
  {
    type: "products",
    eyebrow: "商品与 SKU",
    title: "商品目录导入",
    description: "导入分类、商品、规格、销售单位与人民币含税价。",
    filename: "商品SKU导入模板.xlsx",
    icon: Package,
  },
  {
    type: "purchases",
    eyebrow: "进货与库存",
    title: "进货批量导入",
    description: "按 SKU 编码或商品编码识别进货明细，核对后增加成品仓库库存。",
    filename: "进货入库导入模板.xlsx",
    icon: Stack,
  },
] as const;

export function ImportsAdmin({
  isAdmin,
  allowedTypes = ["customers", "products", "purchases"],
  inventoryEnabled = false,
  initialType,
  notify,
}: {
  isAdmin: boolean;
  allowedTypes?: ImportType[];
  inventoryEnabled?: boolean;
  initialType?: ImportType | undefined;
  notify: (message: string, tone?: "success" | "error") => void;
}) {
  const [focusedType, setFocusedType] = useState(initialType);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [purchaseDraft, setPurchaseDraft] = useState<PurchaseDraftRow[] | null>(null);
  const [draftErrors, setDraftErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [referenceAutoValidationPending, setReferenceAutoValidationPending] = useState(false);
  const [referenceAutoValidating, setReferenceAutoValidating] = useState(false);
  const [referenceAutoValidationError, setReferenceAutoValidationError] = useState<string | null>(null);
  const referenceAutoValidationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const purchaseValidationInFlight = useRef(false);
  const purchaseValidationGeneration = useRef(0);
  const referenceAutoValidationGeneration = useRef(0);
  const [purchaseDefaults, setPurchaseDefaults] = useState<PurchaseDefaults>(() => ({
    occurredOn: todayInShanghai(), referenceNo: "",
  }));
  const purchaseDefaultsRevision = useRef(0);
  const canUpload = (type: ImportType) => isAdmin && allowedTypes.includes(type) && (type !== "purchases" || inventoryEnabled);
  const permissionMessage = (type: ImportType) => `当前账号没有${type === "customers" ? "客户" : type === "products" ? "商品和分类" : "库存"}维护权限，不能上传或确认${type === "customers" ? "客户" : type === "products" ? "商品" : "进货"}导入。`;
  const previewAllowed = preview !== null && canUpload(preview.type);
  const hasPurchaseEdits = preview?.type === "purchases" && purchaseDraft !== null;
  const canEditPurchases = canUpload("purchases");
  const purchasePermission = useRef(canEditPurchases);
  const canConfirm = previewAllowed && preview?.valid && preview.errors.length === 0 && !hasPurchaseEdits;
  const purchaseRows = purchaseDraft ?? purchaseDraftRows(preview?.purchases ?? []);
  const validatedPurchaseRows = new Map(preview?.purchases.map((row, index) => [row.rowIndex ?? index, row]));
  const hasOtherPurchaseEdits = hasPurchaseEdits && (purchaseRows.length !== preview.purchases.length || purchaseRows.some((row) => {
    const original = validatedPurchaseRows.get(row.rowIndex);
    return !original || row.occurredOn !== original.occurredOn || row.skuCode !== original.skuCode
      || row.quantity !== String(original.quantity) || row.note !== original.note;
  }));
  const purchaseReferences = [...new Set(purchaseRows.map((row) => row.referenceNo.trim().toLowerCase()))];
  const hasMixedPurchaseReferences = purchaseReferences.length > 1;
  const unifiedPurchaseReference = hasMixedPurchaseReferences ? "" : purchaseRows[0]?.referenceNo ?? "";
  const hasReferenceChanges = Boolean(purchaseDraft?.some((row) => row.referenceNo !== validatedPurchaseRows.get(row.rowIndex)?.referenceNo));
  const hasManualPurchaseEdits = hasPurchaseEdits && (hasOtherPurchaseEdits || !hasReferenceChanges);
  const purchaseDates = [...new Set(purchaseRows.map((row) => row.occurredOn.trim()))];
  const hasMixedPurchaseDates = purchaseDates.length > 1;
  const unifiedPurchaseDate = hasMixedPurchaseDates ? "" : purchaseRows[0]?.occurredOn ?? "";
  const hasRowHints = purchaseRows.some((row) => row.warnings?.length
    || row.skuCode.trim().toLowerCase() !== validatedPurchaseRows.get(row.rowIndex)?.skuCode.trim().toLowerCase());
  const hasWarnings = Boolean(preview?.warnings.length || preview?.purchases.some((row) => row.warnings?.length));
  const previewTitle = hasManualPurchaseEdits ? "修改尚未校验" : !preview?.valid ? "发现错误，未写入任何数据"
    : hasWarnings ? "校验通过，请核对提示后导入" : "校验通过，可以导入";

  function cancelReferenceAutoValidation() {
    if (referenceAutoValidationTimer.current !== null) clearTimeout(referenceAutoValidationTimer.current);
    referenceAutoValidationTimer.current = null;
    setReferenceAutoValidationPending(false);
    setReferenceAutoValidationError(null);
  }

  function updatePurchaseDefault(key: keyof PurchaseDefaults, value: string) {
    if (loading && preview) return;
    cancelReferenceAutoValidation();
    purchaseValidationGeneration.current += 1;
    purchaseDefaultsRevision.current += 1;
    setPurchaseDefaults((current) => ({ ...current, [key]: value }));
    setPreview((current) => current?.type === "purchases" ? null : current);
    setPurchaseDraft(null);
    setDraftErrors([]);
  }

  function discardPreview() {
    cancelReferenceAutoValidation();
    purchaseValidationGeneration.current += 1;
    setPreview(null);
    setPurchaseDraft(null);
    setDraftErrors([]);
  }

  function updatePurchaseReference(value: string) {
    if (!preview || preview.type !== "purchases" || loading || !canEditPurchases) return;
    if (purchaseRows.every((row) => row.referenceNo === value)) return;
    setDraftErrors([]);
    setReferenceAutoValidationError(null);
    const rows = purchaseRows.map((row) => ({ ...row, referenceNo: value }));
    const unchanged = rows.length === preview.purchases.length && rows.every((row) => {
      const original = validatedPurchaseRows.get(row.rowIndex);
      return original && row.referenceNo === original.referenceNo && row.occurredOn === original.occurredOn
        && row.skuCode === original.skuCode && row.quantity === String(original.quantity) && row.note === original.note;
    });
    setPurchaseDraft(unchanged ? null : rows);
    referenceAutoValidationGeneration.current = purchaseValidationGeneration.current;
    setReferenceAutoValidationPending(rows.some((row) => row.referenceNo !== validatedPurchaseRows.get(row.rowIndex)?.referenceNo));
  }

  function updatePurchaseDate(value: string) {
    if (!preview || preview.type !== "purchases" || loading || !canEditPurchases) return;
    setDraftErrors([]);
    setPurchaseDraft((current) => (current ?? purchaseDraftRows(preview.purchases)).map((row) => ({ ...row, occurredOn: value })));
  }

  function updatePurchaseRow(index: number, key: "skuCode" | "quantity" | "note", value: string) {
    if (!preview || preview.type !== "purchases" || loading || !canEditPurchases) return;
    setDraftErrors([]);
    setPurchaseDraft((current) => {
      const rows = current ?? purchaseDraftRows(preview.purchases);
      const target = rows[index]!;
      return rows.map((row, rowIndex) => {
        const sameDocument = key === "note" && row.occurredOn === target.occurredOn
          && row.referenceNo.trim().toLowerCase() === target.referenceNo.trim().toLowerCase();
        return rowIndex === index || sameDocument ? { ...row, [key]: value } : row;
      });
    });
  }

  function removePurchaseRow(index: number) {
    if (!preview || preview.type !== "purchases" || loading || !canEditPurchases) return;
    setDraftErrors([]);
    setPurchaseDraft((current) => (current ?? purchaseDraftRows(preview.purchases)).filter((_, rowIndex) => rowIndex !== index));
  }

  const revalidatePurchases = useCallback(async (automaticReference = false) => {
    if (referenceAutoValidationTimer.current !== null) clearTimeout(referenceAutoValidationTimer.current);
    referenceAutoValidationTimer.current = null;
    setReferenceAutoValidationPending(false);
    if (!preview || preview.type !== "purchases" || !purchaseDraft || loading || !canEditPurchases || purchaseValidationInFlight.current) return;
    const errors: string[] = [];
    if (purchaseDraft.length === 0) errors.push("请保留至少一行进货明细，可撤销修改恢复。");
    for (const [index, row] of purchaseDraft.entries()) {
      const label = purchaseRowLabel(row, index);
      const quantity = Number(row.quantity);
      if (!row.quantity.trim() || !Number.isInteger(quantity) || quantity <= 0 || quantity > 99_999_999) {
        errors.push(`${label}：进货数量必须为 1 至 99,999,999 的整数`);
      }
      if (!row.skuCode.trim()) errors.push(`${label}：SKU 编码不能为空`);
      if (!row.occurredOn) errors.push(`${label}：请选择进货日期`);
    }
    setDraftErrors(errors);
    if (errors.length > 0) return;
    const generation = purchaseValidationGeneration.current;
    purchaseValidationInFlight.current = true;
    setLoading(true);
    setReferenceAutoValidating(automaticReference);
    setReferenceAutoValidationError(null);
    try {
      const result = await request<ImportPreviewResponse>(`/api/v1/admin/imports/${preview.jobId}/revalidate`, {
        method: "POST",
        body: JSON.stringify({
          revision: preview.revision,
          purchases: purchaseDraft.map((row) => ({
            rowIndex: row.rowIndex,
            occurredOn: row.occurredOn,
            referenceNo: row.referenceNo,
            skuCode: row.skuCode,
            quantity: Number(row.quantity),
            note: row.note,
          })),
        }),
      });
      if (generation !== purchaseValidationGeneration.current || !purchasePermission.current) return;
      setPreview(importPreview("purchases", result));
      setPurchaseDraft(null);
      setDraftErrors([]);
    } catch (error) {
      if (generation === purchaseValidationGeneration.current && purchasePermission.current) {
        const message = error instanceof ApiError ? error.message : "修改校验失败，请重试";
        if (automaticReference) setReferenceAutoValidationError(message);
        notify(automaticReference ? `${message}，可点击“重试校验单号”重试` : message, "error");
      }
    } finally {
      purchaseValidationInFlight.current = false;
      setLoading(false);
      setReferenceAutoValidating(false);
    }
  }, [preview, purchaseDraft, loading, canEditPurchases, notify]);

  useEffect(() => {
    if (!referenceAutoValidationPending || !hasReferenceChanges || !canEditPurchases || loading) return;
    if (referenceAutoValidationGeneration.current !== purchaseValidationGeneration.current) return;
    const timer = setTimeout(() => void revalidatePurchases(true), 700);
    referenceAutoValidationTimer.current = timer;
    return () => {
      clearTimeout(timer);
      if (referenceAutoValidationTimer.current === timer) referenceAutoValidationTimer.current = null;
    };
  }, [referenceAutoValidationPending, hasReferenceChanges, canEditPurchases, loading, revalidatePurchases]);

  useEffect(() => {
    purchasePermission.current = canEditPurchases;
    if (canEditPurchases) return;
    purchaseValidationGeneration.current += 1;
    if (referenceAutoValidationTimer.current !== null) clearTimeout(referenceAutoValidationTimer.current);
    referenceAutoValidationTimer.current = null;
    queueMicrotask(() => setReferenceAutoValidationPending(false));
  }, [canEditPurchases]);

  useEffect(() => () => { purchaseValidationGeneration.current += 1; }, []);

  async function template(type: ImportType, filename: string) {
    try {
      saveBlob(await download(`/api/v1/admin/imports/${type}/template`), filename);
    } catch (error) {
      notify(error instanceof ApiError ? error.message : "模板下载失败", "error");
    }
  }

  async function upload(type: ImportType, file: File) {
    if (loading) return;
    if (!isAdmin || !allowedTypes.includes(type)) {
      notify(!isAdmin ? "当前账号没有导入权限，不能上传或确认导入。" : permissionMessage(type), "error");
      return;
    }
    if (type === "purchases" && !inventoryEnabled) return;
    // A failed replacement file must never leave a previous job available to confirm.
    discardPreview();
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      notify("请选择 .xlsx 格式的 Excel 文件", "error");
      return;
    }
    if (type === "purchases" && !purchaseDefaults.occurredOn) {
      notify("请选择进货日期", "error");
      return;
    }
    const defaultsRevision = purchaseDefaultsRevision.current;
    setLoading(true);
    try {
      const form = new FormData();
      if (type === "purchases") {
        form.append("occurredOn", purchaseDefaults.occurredOn);
        form.append("counterparty", "日丰");
        form.append("referenceNo", purchaseDefaults.referenceNo);
      }
      form.append("file", file);
      const result = await request<ImportPreviewResponse>(
        `/api/v1/admin/imports/${type}/preview`,
        { method: "POST", body: form },
      );
      if (type === "purchases" && defaultsRevision !== purchaseDefaultsRevision.current) {
        notify("进货信息已变更，请重新选择 Excel 进行校验", "error");
        return;
      }
      setPreview(importPreview(type, result));
    } catch (error) {
      notify(error instanceof ApiError ? error.message : "文件解析失败", "error");
    } finally {
      setLoading(false);
    }
  }

  async function confirm() {
    if (!preview || !canConfirm || loading) return;
    setLoading(true);
    setConfirming(true);
    try {
      if (preview.type === "customers") {
        const blob = await download(`/api/v1/admin/imports/${preview.jobId}/confirm`, { method: "POST" });
        saveBlob(blob, "客户一次性初始密码.xlsx");
        notify(`成功导入 ${preview.count} 条数据`);
      } else if (preview.type === "purchases") {
        const result = await request<{ ok: boolean; importedRowCount: number; importedDocumentCount: number }>(
          `/api/v1/admin/imports/${preview.jobId}/confirm`,
          { method: "POST", body: JSON.stringify({ revision: preview.revision }) },
        );
        notify(`成功导入 ${result.importedDocumentCount} 张进货单、${result.importedRowCount} 行明细，成品仓库库存已增加`);
      } else {
        await request(`/api/v1/admin/imports/${preview.jobId}/confirm`, { method: "POST" });
        notify(`成功导入 ${preview.count} 条数据`);
      }
      discardPreview();
    } catch (error) {
      notify(error instanceof ApiError ? error.message : "导入失败", "error");
    } finally {
      setLoading(false);
      setConfirming(false);
    }
  }

  return (
    <section className="import-layout">
      <div className="import-intro">
        <p className="eyebrow">BATCH IMPORT</p>
        <h2>{focusedType === "purchases" ? "进货批量导入" : "先校验，再整批写入"}</h2>
        <p>上传后先检查工作表、必填字段、编码与数量。存在错误时整批不写入；文字不一致等提示可核对后继续。</p>
        {focusedType && <Button variant="secondary" onClick={() => setFocusedType(undefined)}>查看全部批量导入</Button>}
      </div>
      <div className={`import-cards${focusedType ? " import-cards--focused" : ""}`}>
        {importCards.filter((card) => !focusedType || card.type === focusedType).map(({ type, eyebrow, title, description, filename, icon: Icon }) => (
          <article key={type} aria-label={title}>
            <span className="import-card__icon"><Icon /></span>
            <div>
              <small>{eyebrow}</small>
              <h3>{title}</h3>
              <p>{description}</p>
              {type === "purchases" && !inventoryEnabled && (
                <p className="import-card__note">启用成品仓库后可导入进货</p>
              )}
              {isAdmin && !allowedTypes.includes(type) && <p className="import-card__note">{permissionMessage(type)}</p>}
            </div>
            <footer>
              <Button variant="secondary" onClick={() => void template(type, filename)}>
                <FileArrowDown />下载模板
              </Button>
              {canUpload(type) && (
                <label className={`button button--primary${loading ? " import-upload--busy" : ""}`}>
                  <UploadSimple />选择 Excel
                  <input
                    type="file"
                    aria-label={`${title}选择 Excel`}
                    accept=".xlsx"
                    disabled={loading}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) void upload(type, file);
                      event.target.value = "";
                    }}
                  />
                </label>
              )}
            </footer>
          </article>
        ))}
      </div>
      {canUpload("purchases") && preview?.type !== "purchases" && (
        <section className="import-purchase-defaults" aria-label="进货信息">
          <div>
            <h3>进货信息</h3>
            <p>供应商统一为日丰。表格没有日期时使用所选日期，单号可留空。修改后请重新选择 Excel 校验。</p>
            <p>以 SKU 编码或商品编码为准，名称、规格不一致时提示并采用系统档案。必须提供编码；编码不存在或冲突需修正，仅提供商品编码且对应多个 SKU 时需补充 SKU 编码，单位不符需先换算数量。</p>
          </div>
          <div className="import-purchase-defaults__fields">
            <Field label="进货日期">
              <input type="date" value={purchaseDefaults.occurredOn} disabled={loading && Boolean(preview)}
                onChange={(event) => updatePurchaseDefault("occurredOn", event.target.value)} />
            </Field>
            <Field label="进货单号（选填）">
              <input value={purchaseDefaults.referenceNo} placeholder="表格没有单号时使用" disabled={loading && Boolean(preview)}
                onChange={(event) => updatePurchaseDefault("referenceNo", event.target.value)} />
            </Field>
          </div>
        </section>
      )}
      {!isAdmin && <div className="readonly-note">当前账号可以下载模板，但没有上传或确认导入权限。</div>}
      {loading && !preview && <div className="readonly-note" role="status">正在校验 Excel，请稍候…</div>}
      {preview && (
        <div className={`import-preview ${hasManualPurchaseEdits ? "pending" : preview.valid ? "valid" : "invalid"}`}>
          <header>
            {hasManualPurchaseEdits ? <PencilSimple /> : preview.valid ? <Check weight="bold" /> : <XCircle weight="fill" />}
            <div>
              <h3>{previewTitle}</h3>
              <p>
                {hasManualPurchaseEdits ? `修改中 · 剩余 ${purchaseRows.length} 行进货明细，重新校验通过后可确认入库` : preview.type === "purchases"
                  ? `共读取 ${preview.count} 行进货明细 · ${preview.documentCount} 张进货单 · 总数量 ${preview.totalQuantity}`
                  : `共读取 ${preview.count} 条记录`}
              </p>
            </div>
          </header>
          {preview.type === "purchases" && preview.sources.length > 0 && (
            <div className="import-preview__recognition">
              <p>已自动识别原表，可直接使用，无需改成模板。</p>
              {preview.sources.map((source, index) => (
                <p key={index}>
                  {`「${source.sheetName}」第 ${source.headerRow} 行表头：`}
                  {[
                    ...(source.columns.skuCode ? [`${source.columns.skuCode} → SKU 编码`] : []),
                    ...(source.columns.productCode ? [`${source.columns.productCode} → 商品编码`] : []),
                    ...(source.columns.code || source.columns.genericCode ? [`${source.columns.code || source.columns.genericCode} → 编码（SKU／商品）`] : []),
                    ...(source.columns.productName ? [`${source.columns.productName} → 品名`] : []),
                    ...(source.columns.specification ? [`${source.columns.specification} → 规格`] : []),
                    `${source.columns.quantity} → 数量`,
                    ...(source.columns.unit ? [`${source.columns.unit} → 单位`] : []),
                  ].join("；")}
                </p>
              ))}
            </div>
          )}
          {preview.type === "purchases" && preview.warnings.length > 0 && (
            <ul className="import-preview__warnings" aria-label="进货识别提示">
              {preview.warnings.map((warning, index) => <li key={index}>{warning.replace(
                /^未填写进货日期的明细将使用 (\d{4}-\d{2}-\d{2})$/,
                "原表中未填写进货日期的明细，首次识别时使用 $1",
              )}</li>)}
            </ul>
          )}
          {preview.type === "purchases" && (preview.purchases.length > 0 || hasPurchaseEdits) && (
            <>
              <p className="import-preview__stock-note">请核对进货明细，确认后将增加成品仓库库存。</p>
              {canEditPurchases && <p className="import-preview__edit-note">可直接修改 SKU、数量和备注，删除不需要的行。品名、规格与单位按 SKU 重新校验后更新。同一进货单的备注需一致，修改备注会同步到同日期、单号的明细。</p>}
              <div className="import-purchase-reference" role="group" aria-label="进货单据信息">
                <div className="import-purchase-reference__fields">
                <Field label="进货单号（选填）">
                  <input aria-label="统一进货单号（选填）" value={unifiedPurchaseReference} maxLength={100} placeholder={hasMixedPurchaseReferences ? "填写后统一全部明细的单号" : "可留空"}
                    disabled={(loading && !referenceAutoValidating) || !canEditPurchases || purchaseRows.length === 0}
                    readOnly={referenceAutoValidating}
                    onChange={(event) => updatePurchaseReference(event.target.value)} />
                </Field>
                <Field label="进货日期">
                  <input type="date" aria-label="统一进货日期" value={unifiedPurchaseDate}
                    disabled={loading || !canEditPurchases || purchaseRows.length === 0}
                    onChange={(event) => updatePurchaseDate(event.target.value)} />
                </Field>
                </div>
                <div className="import-purchase-reference__status">
                  <p className="import-purchase-reference__hint" role={referenceAutoValidationError ? "alert" : referenceAutoValidationPending || referenceAutoValidating ? "status" : undefined}>
                    {referenceAutoValidationError || (referenceAutoValidating ? "正在自动校验单号…" : referenceAutoValidationPending ? "单号输入完成后将自动校验，请稍候…"
                      : "单号修改后会自动校验；修改日期或明细后请重新校验。")}
                  </p>
                  <Button variant="secondary" className={referenceAutoValidationError ? "" : "import-purchase-reference__retry-hidden"}
                    aria-hidden={!referenceAutoValidationError} tabIndex={referenceAutoValidationError ? 0 : -1}
                    disabled={!referenceAutoValidationError || loading || !canEditPurchases} onClick={() => void revalidatePurchases(true)}>
                    重试校验单号
                  </Button>
                </div>
                {hasMixedPurchaseReferences && <p className="import-purchase-reference__mixed" role="note" aria-label="进货单号提示">
                  {`明细中存在多个进货单号：${[...new Set(purchaseRows.map((row) => row.referenceNo.trim() || "未填写"))].join("、")}。当前保留各行原单号，主动填写上方单号后才会统一。`}
                </p>}
                {hasMixedPurchaseDates && <p className="import-purchase-reference__mixed" role="note" aria-label="进货日期提示">
                  {`明细中存在多个进货日期：${purchaseDates.map((value) => value || "未填写").join("、")}。当前保留各行原日期，主动选择上方日期后才会统一。`}
                </p>}
              </div>
              <div className="import-purchase-table">
                <table>
                  <caption>进货明细预览</caption>
                  <thead><tr>
                    <th scope="col">来源行</th>
                    <th scope="col">SKU 编码</th>
                    <th scope="col">商品名称</th><th scope="col">规格</th><th scope="col">单位</th>
                    <th scope="col">进货数量</th><th scope="col">进货单备注</th>
                    {hasRowHints && <th scope="col">识别提示</th>}
                    {canEditPurchases && <th scope="col">操作</th>}
                  </tr></thead>
                  <tbody>{purchaseRows.map((row, index) => {
                    const label = purchaseRowLabel(row, index);
                    const originalRow = validatedPurchaseRows.get(row.rowIndex);
                    const skuChanged = row.skuCode.trim().toLowerCase() !== originalRow?.skuCode.trim().toLowerCase();
                    return <tr key={row.rowIndex}>
                      <td>{row.sheetName && row.rowNumber ? `${row.sheetName} · 第 ${row.rowNumber} 行` : "—"}</td>
                      <td>
                        <input className="import-purchase-table__sku" aria-label={`${label} SKU 编码`} value={row.skuCode}
                          disabled={loading || !canEditPurchases} onChange={(event) => updatePurchaseRow(index, "skuCode", event.target.value)} />
                        {row.sourceSkuCode && row.sourceSkuCode !== row.skuCode && <small className="import-purchase-table__source">原表 SKU：{row.sourceSkuCode}</small>}
                        {row.sourceProductCode && row.sourceProductCode !== row.productCode && <small className="import-purchase-table__source">原表商品编码：{row.sourceProductCode}</small>}
                        {row.sourceCode && <small className="import-purchase-table__code">原表编码：{row.sourceCode}</small>}
                      </td>
                      <td>
                        {skuChanged ? "待校验" : row.productName || row.sourceProductName || "—"}
                        {!skuChanged && row.sourceProductName && row.productName && row.sourceProductName !== row.productName && (
                          <small className="import-purchase-table__source">{`原表：${row.sourceProductName}`}</small>
                        )}
                      </td>
                      <td>
                        {skuChanged ? "待校验" : row.specification || row.sourceSpecification || "—"}
                        {!skuChanged && row.sourceSpecification && row.specification && row.sourceSpecification !== row.specification && (
                          <small className="import-purchase-table__source">原表规格：{row.sourceSpecification}</small>
                        )}
                      </td>
                      <td>
                        {skuChanged ? "待校验" : row.unit || row.sourceUnit || "—"}
                        {!skuChanged && row.sourceUnit && row.unit && row.sourceUnit !== row.unit && (
                          <small className="import-purchase-table__source">{`原表：${row.sourceUnit}`}</small>
                        )}
                      </td>
                      <td><input type="number" className="import-purchase-table__quantity" aria-label={`${label} 进货数量`} value={row.quantity}
                        min={1} max={99_999_999} step={1} disabled={loading || !canEditPurchases}
                        onChange={(event) => updatePurchaseRow(index, "quantity", event.target.value)} /></td>
                      <td><input className="import-purchase-table__note" aria-label={`${label} 进货单备注`} value={row.note}
                        maxLength={500} disabled={loading || !canEditPurchases} onChange={(event) => updatePurchaseRow(index, "note", event.target.value)} /></td>
                      {hasRowHints && <td className="import-purchase-table__warnings">
                        {skuChanged ? "SKU 已修改，待重新校验" : row.warnings?.length ? <ul aria-label={`第 ${row.rowNumber ?? index + 1} 行提示`}>
                          {row.warnings.map((warning, warningIndex) => <li key={warningIndex}>{warning}</li>)}
                        </ul> : "—"}
                      </td>}
                      {canEditPurchases && <td><Button variant="ghost" disabled={loading} aria-label={`删除 ${label}`}
                        onClick={() => removePurchaseRow(index)}>删除</Button></td>}
                    </tr>;
                  })}</tbody>
                </table>
              </div>
              {hasPurchaseEdits && purchaseRows.length === 0 && <p className="import-preview__empty-draft" role="status">请保留至少一行进货明细，可撤销修改恢复。</p>}
            </>
          )}
          {!hasPurchaseEdits && preview.errors.length > 0 && <ul>{preview.errors.map((error, index) => <li key={index}>{error}</li>)}</ul>}
          {draftErrors.length > 0 && <ul aria-label="修改校验错误">{draftErrors.map((error, index) => <li key={index}>{error}</li>)}</ul>}
          <footer>
            <Button key="discard" variant="secondary" disabled={loading} onClick={discardPreview}>放弃本次预览</Button>
            {hasManualPurchaseEdits && canEditPurchases && <>
              <Button variant="secondary" disabled={loading} onClick={() => { cancelReferenceAutoValidation(); setPurchaseDraft(null); setDraftErrors([]); }}>撤销修改</Button>
              <Button loading={loading} disabled={purchaseRows.length === 0} onClick={() => void revalidatePurchases()}>重新校验修改</Button>
            </>}
            {(preview.type === "purchases" ? previewAllowed : canConfirm) && (
              <Button key="confirm" loading={preview.type === "purchases" ? confirming : loading} disabled={!canConfirm || loading} onClick={() => void confirm()}>
                {preview.type === "purchases" ? "确认导入并增加库存" : "确认写入系统"}
              </Button>
            )}
          </footer>
        </div>
      )}
    </section>
  );
}
